/**
 * Cross-field date/time validation rules for the event modal.
 *
 * All rules are expressed relative to the event's Time Declared
 * (`timeDeclaredZulu`) and, when the event is Completed, its ERTS
 * (`returnToServiceTimeZulu`). Values are parsed with the shared
 * {@link parseFlexibleDate} helper so both "YYYY-MM-DD HH:MM[:SS]" and ISO
 * "YYYY-MM-DDTHH:MM:SSZ" formats are accepted. Incomplete/unparseable values
 * never report a violation here — completeness is enforced by the existing
 * required-field checks.
 */
import { parseFlexibleDate } from '../../utils/dateUtils';

/** Maximum minutes Next Advise may sit before the current time. */
export const NEXT_ADVISE_MAX_MINUTES_IN_PAST = 90;

const MILLISECONDS_PER_MINUTE = 60 * 1000;

/** Error messages surfaced for each temporal rule. */
export const TIME_DECLARED_VIOLATION_MESSAGE = 'Must be later than the Time Declared.';
export const ERTS_CEILING_VIOLATION_MESSAGE =
  'Cannot be after the ERTS once the event is Completed.';
export const NEXT_ADVISE_PAST_VIOLATION_MESSAGE =
  'Cannot be more than 90 minutes before the current time.';

/** Context shared by the field-level validators. */
export interface TimeRuleContext {
  /** The event's Time Declared (`timeDeclaredZulu`). */
  timeDeclared?: string;
  /** The event's ERTS (`returnToServiceTimeZulu`). */
  erts?: string;
  /** Whether the event status is Completed. */
  isCompleted: boolean;
  /**
   * Whether a Completed By alias has been logged. Once set, the event is
   * finalized and Next Advise is expected to be in the past, so the 90-minute
   * past advisory (rule 3) is no longer shown.
   */
  completedByAliasLogged?: boolean;
}

/** Parses a datetime string to epoch milliseconds, or null when unusable. */
function toMillis(value: string | undefined): number | null {
  const parsed = parseFlexibleDate(value || '');
  return parsed ? parsed.getTime() : null;
}

/**
 * Rule 1 — a value at or before the Time Declared is invalid. Returns false
 * unless both values parse, so incomplete entries are never flagged here.
 */
export function isAtOrBeforeTimeDeclared(
  value: string | undefined,
  timeDeclared: string | undefined,
): boolean {
  const valueMillis = toMillis(value);
  const declaredMillis = toMillis(timeDeclared);
  if (valueMillis === null || declaredMillis === null) return false;
  return valueMillis <= declaredMillis;
}

/**
 * Strictly-before variant of {@link isAtOrBeforeTimeDeclared}, used for the
 * first note which is allowed to match the Time Declared exactly (rule 2).
 */
export function isBeforeTimeDeclared(
  value: string | undefined,
  timeDeclared: string | undefined,
): boolean {
  const valueMillis = toMillis(value);
  const declaredMillis = toMillis(timeDeclared);
  if (valueMillis === null || declaredMillis === null) return false;
  return valueMillis < declaredMillis;
}

/** True when `value` parses to exactly the Time Declared instant. */
export function equalsTimeDeclared(
  value: string | undefined,
  timeDeclared: string | undefined,
): boolean {
  return (
    isAtOrBeforeTimeDeclared(value, timeDeclared) && !isBeforeTimeDeclared(value, timeDeclared)
  );
}

/**
 * Rule 4 — a value strictly after the ERTS is invalid (only when Completed).
 */
export function isAfterErts(value: string | undefined, erts: string | undefined): boolean {
  const valueMillis = toMillis(value);
  const ertsMillis = toMillis(erts);
  if (valueMillis === null || ertsMillis === null) return false;
  return valueMillis > ertsMillis;
}

/**
 * Rule 3 — a value more than {@link NEXT_ADVISE_MAX_MINUTES_IN_PAST} minutes
 * before `now` is invalid.
 */
export function isTooFarInPast(value: string | undefined, now: number = Date.now()): boolean {
  const valueMillis = toMillis(value);
  if (valueMillis === null) return false;
  return valueMillis < now - NEXT_ADVISE_MAX_MINUTES_IN_PAST * MILLISECONDS_PER_MINUTE;
}

/**
 * Validates the ERTS value. ERTS must be later than the Time Declared (rule 1).
 */
export function validateErts(value: string | undefined, context: TimeRuleContext): string | null {
  if (isAtOrBeforeTimeDeclared(value, context.timeDeclared)) return TIME_DECLARED_VIOLATION_MESSAGE;
  return null;
}

/**
 * Validates the Next Advise value: later than Time Declared (rule 1), not more
 * than 90 minutes before now until the event is finalized (rule 3 — suppressed
 * once the status is Completed or a Completed By alias is logged), and not
 * after the ERTS once Completed (rule 4).
 *
 * `enforcePastLimit` gates rule 3 so it can be applied only when the field is
 * actually being changed — a pre-existing, untouched Next Advise that has
 * naturally aged past 90 minutes must not block saving an open event.
 */
export function validateNextAdvise(
  value: string | undefined,
  context: TimeRuleContext,
  options: { now?: number; enforcePastLimit?: boolean } = {},
): string | null {
  const now = options.now ?? Date.now();
  const enforcePastLimit = options.enforcePastLimit ?? true;
  if (isAtOrBeforeTimeDeclared(value, context.timeDeclared)) return TIME_DECLARED_VIOLATION_MESSAGE;
  const finalized = context.isCompleted || !!context.completedByAliasLogged;
  if (enforcePastLimit && !finalized && isTooFarInPast(value, now))
    return NEXT_ADVISE_PAST_VIOLATION_MESSAGE;
  if (context.isCompleted && isAfterErts(value, context.erts))
    return ERTS_CEILING_VIOLATION_MESSAGE;
  return null;
}

/**
 * Validates a Maintenance Barrier or Note date/time: later than Time Declared
 * (rule 1) and not after the ERTS once Completed (rule 4).
 *
 * The first note (the event-declaration note) is permitted to match the Time
 * Declared exactly — pass `allowEqualToTimeDeclared` for that case (rule 2).
 */
export function validateWithinEventWindow(
  value: string | undefined,
  context: TimeRuleContext,
  options: { allowEqualToTimeDeclared?: boolean } = {},
): string | null {
  const beforeTimeDeclared = options.allowEqualToTimeDeclared
    ? isBeforeTimeDeclared(value, context.timeDeclared)
    : isAtOrBeforeTimeDeclared(value, context.timeDeclared);
  if (beforeTimeDeclared) return TIME_DECLARED_VIOLATION_MESSAGE;
  if (context.isCompleted && isAfterErts(value, context.erts))
    return ERTS_CEILING_VIOLATION_MESSAGE;
  return null;
}

/**
 * Validates an Audit entry date/time: later than the Time Declared (rule 1).
 */
export function validateAuditEntry(
  value: string | undefined,
  context: TimeRuleContext,
): string | null {
  if (isAtOrBeforeTimeDeclared(value, context.timeDeclared)) return TIME_DECLARED_VIOLATION_MESSAGE;
  return null;
}
